<template>
  <div>
    <div
      :class="[
        $style.listingImageGalleryContainer,
        { [$style.noImages]: !galleryListingImages },
        $style[theme],
      ]"
    >
      <ImageGallery
        v-if="galleryListingImages"
        :images="galleryListingImages"
        :activeSlideIndex="activeSlideIndex"
        :nextTitle="t('nextImage').toString()"
        :prevTitle="t('previousImage').toString()"
        :shouldShowNewConstructionBadge="shouldShowNewConstructionBadge"
        :isArrowKeysEnabled="!isModalOpen"
        :overlayStartIndex="overlayStartIndex"
        :exclusiveStartIndex="exclusiveImageStartIndex"
        @imageClick="openModal"
        @slideChange="changeActiveSlide"
        @moreImagesClick="onExclusiveImagesModalClick()"
        @contactEmailClick="requestContactForm"
      >
        <template #default="{ totalSlides }">
          <div :class="$style.slidesCounterPositioningWrapper">
            <HgSlidesCounter :activeSlideIndex="activeSlideIndex" :totalSlides="totalSlides" />
          </div>
        </template>
      </ImageGallery>
      <div v-else :class="$style.imagePlaceholderContainer">
        <ImagePlaceholder />
        <div :class="$style.placeholderLabelMain">
          {{ t('imageGallery.noImageAvailable') }}
        </div>
        <div :class="$style.placeholderLabelSecondary">
          {{ t('imageGallery.contactAdvertisers') }}
        </div>
      </div>
    </div>
    <ListingDetailsModal
      v-if="(galleryListingImages || virtualTour || videoMovie) && isModalLoaded"
      :listingImages="fullScreenGalleryListingImages"
      :virtualTour="virtualTour"
      :videoMovie="videoMovie"
      :activeSlideIndex="activeSlideIndex"
      @slideChange="changeActiveSlide"
      @moreImagesClick="onExclusiveImagesModalClick()"
      @contactEmailClick="requestContactForm"
    />
    <TenantPlusExclusiveImagesModal
      v-if="showExclusiveImagesModal"
      :translations="tenantPlusExclusiveTranslations"
      :imageSrc="exclusiveModalImage"
      :startTrialUrl="tenantPlusPaymentUrl"
      :learnMoreUrl="tenantPlusLandingPageUrl"
      :alreadyTenantPlusUserUrl="signinUrl"
      :modalOpenTrigger="tenantPlusModalOpenTrigger"
      :theme="exclusiveImageModalTheme"
      :class="$style.tenantPlusExclusiveImagesModal"
      @close="onCloseTenantPlusExclusiveImagesModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgSlidesCounter, IconTheme, SupportedPlatforms } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { computed, defineAsyncComponent, ref } from 'vue';

import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import {
  getCloudinaryUrlForTransformation,
  getCloudinaryUrlVersions,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import { getCurrentPlatformTheme, isPlatformHg } from '@/helpers/platformDetection';
import {
  TenantPlusFeature,
  TenantPlusModalOpenTrigger,
} from '@/helpers/TenantPlus/TenantPlusModalOpenTriggers';
import {
  generateTenantPlusLandingPageUrl,
  generateTenantPlusManualPaymentUrl,
  generateTenantPlusSigninUrl,
  generateTenantPlusTrackingEntryPoint,
} from '@/helpers/TenantPlus/TenantPlusUrls';
import { HgListingVideoMovie, HgListingVirtualTour, ListingDetailsModalTabId } from '@/models';
import { useListingStore } from '@/store/modules/listing';
import { useListingImageGalleryStore } from '@/store/modules/listing-image-gallery';
import type { HgImage } from '@/store/types';
import { TenantPlusExclusiveImagesModal } from '@smg-real-estate/seeker-app-shared-components';
import { useI18n } from 'vue-i18n';

const listingStore = useListingStore();
const { t, locale } = useI18n();
const listingImageGalleryStore = useListingImageGalleryStore();

defineOptions({
  name: 'ListingMediaGallery',
});

const ImagePlaceholder = defineAsyncComponent(
  () => import('@smg-real-estate/domus/assets/images/image-placeholder.svg'),
);
const ListingDetailsModal = defineAsyncComponent(
  () => import('@/components/ListingDetailsModal/ListingDetailsModal.vue'),
);

interface Props {
  listingImages?: HgImage[] | undefined;
  virtualTour?: HgListingVirtualTour | undefined;
  videoMovie?: HgListingVideoMovie | undefined;
  shouldShowNewConstructionBadge?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  listingImages: undefined,
  virtualTour: undefined,
  videoMovie: undefined,
  shouldShowNewConstructionBadge: false,
});

const emit = defineEmits<{
  moreImagesClick: [];
}>();

const activeSlideIndex = ref(0);
const showExclusiveImagesModal = ref(false);
const exclusiveModalImage = ref('');
const exclusiveImageModalTheme = ref<IconTheme>(IconTheme.Current);
const theme = getCurrentPlatformTheme();

const getGalleryResponsiveImagesForSizes = (sizes: string) => {
  return (
    props.listingImages &&
    props.listingImages.map((image: HgImage) => {
      return {
        src: getCloudinaryUrlForTransformation(image.src, HgCloudinaryNamedTransformation.SMALL),
        alt: image.alt,
        caption: image.caption,
        srcset: getCloudinaryUrlVersions(image.src),
        sizes,
      };
    })
  );
};

const isModalOpen = computed((): boolean => {
  return listingImageGalleryStore.isModalOpen;
});
const isModalLoaded = computed((): boolean => {
  return listingImageGalleryStore.isModalLoaded;
});
const galleryListingImages = computed((): HgImage[] | undefined => {
  return getGalleryResponsiveImagesForSizes('(min-width: 1280px) 800px, 100vw');
});
const fullScreenGalleryListingImages = computed((): HgImage[] | undefined => {
  return getGalleryResponsiveImagesForSizes('100vw');
});
const overlayStartIndex = computed((): number => {
  return listingStore.exclusiveBlurryImageStartIndex;
});
const exclusiveImageStartIndex = computed((): number => {
  return listingStore.exclusiveImageStartIndex;
});
const platform = computed(() => {
  return isPlatformHg() ? SupportedPlatforms.hg : SupportedPlatforms.is24;
});
const tenantPlusExclusiveTranslations = computed(() => {
  return {
    header: t('tenantPlus.exclusiveImagesModal.variable.header'),
    benefits: {
      benefit1: t('tenantPlus.signUpRequiredModal.benefit1'),
      benefit2: t('tenantPlus.signUpRequiredModal.benefit2'),
      benefit3: t('tenantPlus.signUpRequiredModal.benefit3'),
      benefit4: t('tenantPlus.signUpRequiredModal.benefit4'),
      benefit5: t('tenantPlus.signUpRequiredModal.benefit5'),
    },
    benefitCaption: t('tenantPlus.signUpRequiredModal.captionBenefit5'),
    startFreeTrialCTA: t('tenantPlus.signUpRequiredModal.startFreeTrialCTA'),
    learnMoreCTA: t('tenantPlus.signUpRequiredModal.learnMoreCTA'),
    alreadyTenantPlusUserCTA: t('tenantPlus.signUpRequiredModal.alreadyTenantPlusUserCTA'),
  };
});
const signinUrl = computed((): string => {
  return generateTenantPlusSigninUrl(locale.value);
});
const getEntryPoint = computed((): string => {
  return generateTenantPlusTrackingEntryPoint(
    listingStore.listing?.offerType,
    TenantPlusModalOpenTrigger.MORE_IMAGES,
  );
});
const tenantPlusLandingPageUrl = computed((): string => {
  return generateTenantPlusLandingPageUrl(locale.value, getEntryPoint.value);
});
const tenantPlusPaymentUrl = computed((): string => {
  return generateTenantPlusManualPaymentUrl(
    locale.value,
    getEntryPoint.value,
    TenantPlusFeature.EXCLUSIVE_IMAGES,
  );
});
const tenantPlusModalOpenTrigger = computed((): string => {
  return TenantPlusModalOpenTrigger.MORE_IMAGES;
});

useHead(() => {
  const images = galleryListingImages.value;
  if (!images || !images[0]) {
    return {};
  }

  const { src: href, sizes: imagesizes, srcset: imagesrcset } = images[0];

  return {
    link: [
      {
        href,
        imagesizes,
        as: 'image',
        imagesrcset,
        rel: 'preload',
        fetchpriority: 'high',
      },
    ],
  };
});

const changeActiveSlide = (newActiveImageIndex: number) => {
  activeSlideIndex.value = newActiveImageIndex;
};
const openModal = () => {
  listingImageGalleryStore.setSelectedModalTabId(
    ListingDetailsModalTabId.MODAL_TAB_ID_IMAGEGALLERY,
  );
  listingImageGalleryStore.openModal();
};
const requestContactForm = () => {
  listingImageGalleryStore.requestOpenContactForm();
};
const onExclusiveImagesModalClick = () => {
  showExclusiveImagesModal.value = true;
};
const onCloseTenantPlusExclusiveImagesModal = () => {
  showExclusiveImagesModal.value = false;
};
const setExclusiveImagesModalImage = async () => {
  const image = await import(
    platform.value === SupportedPlatforms.is24
      ? '@/assets/TenantPlus/tenant-plus-more-images-visual-is24.webp?url'
      : '@/assets/TenantPlus/tenant-plus-more-images-visual.webp?url'
  );

  exclusiveModalImage.value = image.default;
};

setExclusiveImagesModalImage();
</script>

<style lang="scss" module>
@import '@/assets/variables';

$image-gallery-height: 500px;
$placeholder-image-top-offset-mobile: 42px;
$placeholder-image-top-offset-desktop: 104px;
$placeholder-image-width: 140px;
$placeholder-image-height: 182px;

.slidesCounterPositioningWrapper {
  display: flex;
  align-self: center;
  justify-content: center;
  width: 100%;

  .hg & {
    justify-content: end;
  }
}

.listingImageGalleryContainer {
  position: relative;
  padding-bottom: 75%; // 4:3 ratio
  background-color: $color-grey-30;

  &.noImages {
    padding-bottom: 100%;
  }
}

.imagePlaceholderContainer {
  position: absolute;
  top: 50%;
  left: 50%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 $space-100;

  @include body-2;

  text-align: center;
  transform: translate(-50%, -50%);

  svg {
    width: $placeholder-image-width;
    height: $placeholder-image-height;
  }

  .placeholderLabelMain {
    margin-top: $space-100;

    @include font-bold;
  }

  .placeholderLabelSecondary {
    margin-top: $space-50;
  }
}

@media (min-width: $viewport-md-min) {
  .listingImageGalleryContainer,
  .listingImageGalleryContainer.noImages {
    padding-bottom: $image-gallery-height;
  }

  .imagePlaceholderContainer {
    .placeholderLabelMain {
      @include body-1;
    }
  }
}

.tenantPlusExclusiveImagesModal {
  z-index: 1000;
}

@media print {
  .listingImageGalleryContainer {
    padding-bottom: $image-gallery-height;
  }
}
</style>
